import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import validator from "validator";

import {
  Apartment as ApartmentIcon,
  Close as CloseIcon,
  PersonOutline as PersonOutlineIcon,
  Save as SaveIcon
} from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { collection, doc, getDoc, getDocs, setDoc } from "firebase/firestore";

import { useAuth } from "../contexts/AuthContext";
import { db } from "../firebase";

const getInitialFormData = () => ({
  clientType: "individual",
  firstname: "",
  lastname: "",
  email: "",
  phone: "",
  userAddress: "",
  userNote: "",
  company: "",
  siret: "",
  source: "",
  sourceOther: ""
});

const CustomerForm = ({ open, handleClose, customerId, userId, onSave }) => {
  const { currentUser } = useAuth();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const resolvedUserId = userId || currentUser?.uid;

  const [formData, setFormData] = useState(getInitialFormData());
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [sources, setSources] = useState([]);

  useEffect(() => {
    const fetchCustomer = async () => {
      if (!resolvedUserId || !customerId) {
        setFormData(getInitialFormData());
        return;
      }

      setLoading(true);
      try {
        const customerDoc = doc(db, `users/${resolvedUserId}/customers`, customerId);
        const docSnap = await getDoc(customerDoc);

        if (docSnap.exists()) {
          const customerData = docSnap.data();
          setFormData({
            ...getInitialFormData(),
            ...customerData,
            clientType: customerData.company ? "company" : "individual"
          });
        }
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      fetchCustomer();
    }
  }, [customerId, open, resolvedUserId]);

  useEffect(() => {
    const fetchSources = async () => {
      if (!resolvedUserId) return;

      const sourcesCollection = collection(db, `users/${resolvedUserId}/customerSource`);
      const sourcesSnapshot = await getDocs(sourcesCollection);
      const sourcesList = sourcesSnapshot.docs.map(currentDoc => ({
        id: currentDoc.id,
        name: currentDoc.data().name
      }));
      setSources(sourcesList);
    };

    if (open) {
      fetchSources();
    }
  }, [open, resolvedUserId]);

  const handleChange = event => {
    const { name, value } = event.target;

    setFormData(prevData => ({
      ...prevData,
      [name]: value,
      sourceOther: name === "source" && value !== "other" ? "" : prevData.sourceOther
    }));

    setErrors(prevErrors => ({
      ...prevErrors,
      [name]: ""
    }));
  };

  const handleClientTypeChange = clientType => {
    setFormData(prevData => ({
      ...prevData,
      clientType,
      company: clientType === "individual" ? "" : prevData.company,
      siret: clientType === "individual" ? "" : prevData.siret
    }));

    setErrors(prevErrors => ({
      ...prevErrors,
      company: ""
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstname.trim()) {
      newErrors.firstname = t("clients.validation.nameRequired");
    }

    if (!formData.email.trim()) {
      newErrors.email = t("clients.validation.emailRequired");
    } else if (!validator.isEmail(formData.email)) {
      newErrors.email = t("clients.validation.emailInvalid");
    }

    if (!formData.phone.trim()) {
      newErrors.phone = t("clients.validation.phoneRequired");
    }

    if (formData.clientType === "company" && !formData.company.trim()) {
      newErrors.company = t("clients.validation.companyRequired");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm() || !resolvedUserId) return;

    setLoading(true);
    const newCustomerId = customerId || new Date().getTime().toString();
    const customerRef = doc(db, `users/${resolvedUserId}/customers`, newCustomerId);

    const payload = {
      ...formData,
      company: formData.clientType === "company" ? formData.company : "",
      siret: formData.clientType === "company" ? formData.siret : ""
    };

    try {
      await setDoc(customerRef, payload, { merge: true });
      enqueueSnackbar(t("clients.notifications.saveSuccess"), {
        variant: "success"
      });

      onSave({ id: newCustomerId, ...payload });
      handleClose();
    } catch (error) {
      enqueueSnackbar(t("clients.notifications.saveError") + error.message, {
        variant: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden"
        }
      }}
    >
      <DialogTitle
        sx={{
          px: 4,
          py: 2,
          borderBottom: theme => `1px solid ${theme.palette.divider}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {customerId ? t("clients.form.editTitle") : t("clients.form.createTitle")}
        </Typography>
        <IconButton onClick={handleClose} sx={{ backgroundColor: "action.hover" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 2.5, md: 4 }, py: 3 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={240}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={3}>
            <Box
              sx={{
                py: 2,
                px: { xs: 1.5, md: 2.5 },
                borderRadius: 2,
                border: theme => `1px solid ${theme.palette.divider}`,
                backgroundColor: theme =>
                  theme.palette.mode === "dark"
                    ? "rgba(148, 163, 184, 0.12)"
                    : "rgba(15, 23, 42, 0.03)"
              }}
            >
              <Typography variant="body1" sx={{ mb: 1.25, fontWeight: 600 }}>
                {t("clients.form.clientTypeLabel")}
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
                <Button
                  variant={formData.clientType === "company" ? "contained" : "outlined"}
                  startIcon={<ApartmentIcon />}
                  onClick={() => handleClientTypeChange("company")}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    justifyContent: "flex-start",
                    px: 2.25,
                    py: 1,
                    borderColor: theme => theme.palette.divider,
                    color: theme => theme.palette.text.primary,
                    backgroundColor:
                      formData.clientType === "company"
                        ? theme =>
                          theme.palette.mode === "dark"
                            ? "rgba(148, 163, 184, 0.25)"
                            : "rgba(15, 23, 42, 0.08)"
                        : "transparent",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor:
                        formData.clientType === "company"
                          ? theme =>
                            theme.palette.mode === "dark"
                              ? "rgba(148, 163, 184, 0.3)"
                              : "rgba(15, 23, 42, 0.12)"
                          : theme =>
                            theme.palette.mode === "dark"
                              ? "rgba(148, 163, 184, 0.1)"
                              : "rgba(15, 23, 42, 0.04)",
                      boxShadow: "none"
                    }
                  }}
                >
                  {t("clients.form.clientType.company")}
                </Button>
                <Button
                  variant={formData.clientType === "individual" ? "contained" : "outlined"}
                  startIcon={<PersonOutlineIcon />}
                  onClick={() => handleClientTypeChange("individual")}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    justifyContent: "flex-start",
                    px: 2.25,
                    py: 1,
                    borderColor: theme => theme.palette.divider,
                    color: theme => theme.palette.text.primary,
                    backgroundColor:
                      formData.clientType === "individual"
                        ? theme =>
                          theme.palette.mode === "dark"
                            ? "rgba(148, 163, 184, 0.25)"
                            : "rgba(15, 23, 42, 0.08)"
                        : "transparent",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor:
                        formData.clientType === "individual"
                          ? theme =>
                            theme.palette.mode === "dark"
                              ? "rgba(148, 163, 184, 0.3)"
                              : "rgba(15, 23, 42, 0.12)"
                          : theme =>
                            theme.palette.mode === "dark"
                              ? "rgba(148, 163, 184, 0.1)"
                              : "rgba(15, 23, 42, 0.04)",
                      boxShadow: "none"
                    }
                  }}
                >
                  {t("clients.form.clientType.individual")}
                </Button>
              </Stack>
            </Box>

            {formData.clientType === "company" && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={8}>
                  <TextField
                    fullWidth
                    label={t("clients.company")}
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    error={!!errors.company}
                    helperText={errors.company}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label={t("clients.form.siret")}
                    name="siret"
                    value={formData.siret}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>
            )}

            <Divider />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label={t("clients.form.firstName")}
                  name="firstname"
                  value={formData.firstname}
                  onChange={handleChange}
                  error={!!errors.firstname}
                  helperText={errors.firstname}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t("clients.form.lastName")}
                  name="lastname"
                  value={formData.lastname}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label={t("clients.email")}
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  error={!!errors.email}
                  helperText={errors.email}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label={t("clients.phone")}
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  error={!!errors.phone}
                  helperText={errors.phone}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("clients.address")}
                  name="userAddress"
                  value={formData.userAddress}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label={t("clients.note")}
                  name="userNote"
                  value={formData.userNote}
                  onChange={handleChange}
                />
              </Grid>
            </Grid>

            <Divider />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label={t("clients.source")}
                  name="source"
                  value={formData.source}
                  onChange={handleChange}
                >
                  <MenuItem value="" disabled>
                    {t("clients.form.selectSource")}
                  </MenuItem>
                  {sources.map(source => (
                    <MenuItem key={source.id} value={source.name}>
                      {source.name}
                    </MenuItem>
                  ))}
                  <MenuItem value="other">{t("clients.form.otherSource")}</MenuItem>
                </TextField>
              </Grid>
              {formData.source === "other" && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t("clients.sourceOther")}
                    name="sourceOther"
                    value={formData.sourceOther}
                    onChange={handleChange}
                  />
                </Grid>
              )}
            </Grid>
          </Stack>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 4,
          py: 2,
          borderTop: theme => `1px solid ${theme.palette.divider}`,
          justifyContent: "space-between"
        }}
      >
        <Button onClick={handleClose} color="inherit" sx={{ textTransform: "none" }}>
          {t("common.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={loading}
          endIcon={<SaveIcon />}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 2,
            px: 2.5,
            py: 1
          }}
        >
          {customerId ? t("common.save") : t("clients.form.submitCreate")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomerForm;
